/**
 * Utility functions for multi-truck attribution calculations.
 * All monetary calculations work in cents to avoid floating-point issues.
 */

/**
 * Splits totalCents evenly across N trucks, distributing remainder to first N trucks.
 * Example: 10000 cents / 3 = [3334, 3333, 3333]
 *
 * @param totalCents - Total amount in cents to split
 * @param count - Number of ways to split
 * @returns Array of cent amounts that sum exactly to totalCents
 */
export function splitEvenlyCents(totalCents: number, count: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [totalCents];

  const base = Math.floor(totalCents / count);
  const remainder = totalCents % count;

  return Array.from(
    { length: count },
    (_, i) => base + (i < remainder ? 1 : 0)
  );
}

/**
 * Converts percentage inputs to cents, adjusting last value to match total exactly.
 * Handles rounding errors by distributing the difference to the last non-zero value.
 *
 * @param percentages - Array of percentage values (0-100)
 * @param totalCents - Total amount in cents
 * @returns Array of cent amounts that sum exactly to totalCents
 */
export function percentagesToCents(
  percentages: number[],
  totalCents: number
): number[] {
  if (percentages.length === 0) return [];

  const rawCents = percentages.map((pct) =>
    Math.round((pct / 100) * totalCents)
  );
  const sum = rawCents.reduce((acc, c) => acc + c, 0);
  const diff = totalCents - sum;

  // Distribute difference to the last non-zero value, or last value if all are zero
  if (diff !== 0 && rawCents.length > 0) {
    // Find the last index with a non-zero percentage to adjust
    let adjustIndex = rawCents.length - 1;
    for (let i = rawCents.length - 1; i >= 0; i--) {
      if (percentages[i] > 0) {
        adjustIndex = i;
        break;
      }
    }
    rawCents[adjustIndex] += diff;
  }

  return rawCents;
}

/**
 * Converts cent amounts to percentages for display.
 *
 * @param amounts - Array of amounts in cents
 * @param totalCents - Total amount in cents
 * @returns Array of percentage values (0-100) rounded to 2 decimal places
 */
export function centsToPercentages(
  amounts: number[],
  totalCents: number
): number[] {
  if (totalCents === 0 || amounts.length === 0) {
    return amounts.map(() => 0);
  }

  return amounts.map((amount) => {
    const percentage = (amount / totalCents) * 100;
    return Math.round(percentage * 100) / 100; // Round to 2 decimal places
  });
}

/**
 * Validates that allocations don't exceed the total transaction amount.
 *
 * @param amounts - Array of amounts in cents
 * @param totalCents - Total transaction amount in cents
 * @returns Validation result with isValid flag and optional error message
 */
export function validateAllocations(
  amounts: number[],
  totalCents: number
): { isValid: boolean; error: string | null } {
  const sum = amounts.reduce((acc, amount) => acc + amount, 0);

  if (sum > totalCents) {
    return {
      isValid: false,
      error: `Total allocations ($${(sum / 100).toFixed(2)}) exceed transaction amount ($${(totalCents / 100).toFixed(2)})`,
    };
  }

  // Check for negative values
  const hasNegative = amounts.some((amount) => amount < 0);
  if (hasNegative) {
    return {
      isValid: false,
      error: 'Allocations cannot be negative',
    };
  }

  return { isValid: true, error: null };
}

/**
 * Calculates the unattributed amount in cents.
 *
 * @param amounts - Array of attributed amounts in cents
 * @param totalCents - Total transaction amount in cents
 * @returns Unattributed amount in cents (0 if fully attributed)
 */
export function calculateUnattributedCents(
  amounts: number[],
  totalCents: number
): number {
  const sum = amounts.reduce((acc, amount) => acc + amount, 0);
  return Math.max(0, totalCents - sum);
}

/**
 * Formats cents to a dollar string for display.
 *
 * @param cents - Amount in cents
 * @param includeSign - Whether to include +/- sign
 * @returns Formatted dollar string (e.g., "$1,234.56")
 */
export function formatCentsToDollars(
  cents: number,
  includeSign = false
): string {
  const dollars = Math.abs(cents) / 100;
  const formatted = dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (includeSign && cents !== 0) {
    return cents < 0 ? `-${formatted}` : `+${formatted}`;
  }

  return formatted;
}

/**
 * Parses a dollar input string to cents.
 * Handles various formats: "$1,234.56", "1234.56", "1234", etc.
 *
 * @param dollarString - Dollar amount as string
 * @returns Amount in cents, or 0 if invalid
 */
export function parseDollarsToCents(dollarString: string): number {
  // Remove currency symbol, commas, and whitespace
  const cleaned = dollarString.replace(/[$,\s]/g, '');
  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return 0;
  }

  // Convert to cents and round to avoid floating-point issues
  return Math.round(parsed * 100);
}

/**
 * Color palette for truck attribution visualization.
 * Colors are selected to be visually distinct and accessible.
 */
export const TRUCK_COLORS = [
  '#1D213B', // Primary (navy)
  '#6A9E68', // Secondary (green)
  '#F7A826', // Accent (orange)
  '#0390F4', // Info (blue)
  '#C64949', // Danger (red)
  '#676C8B', // Gray secondary
  '#5a8558', // Dark green
  '#D4A017', // Gold
  '#4A90D9', // Light blue
  '#8B5CF6', // Purple
];

export const UNATTRIBUTED_COLOR = '#E6E6E6'; // Light gray

/**
 * Gets a color for a truck at a given index.
 * Cycles through the color palette if index exceeds available colors.
 *
 * @param index - Index of the truck in the list
 * @returns Hex color string
 */
export function getTruckColor(index: number): string {
  return TRUCK_COLORS[index % TRUCK_COLORS.length];
}

/**
 * NULL_UUID used for unattributed portions of transactions.
 */
export const NULL_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * Builds the initial truck selection for a transaction's truck editor.
 * Prefers explicit multi-truck attributions — dropping the NULL_UUID Business
 * sentinel, which marks the no-truck portion and is not a selectable truck —
 * then falls back to the legacy single truck_id, else an empty selection
 * (which the editor renders as "Business").
 *
 * Shared by the banking transactions table and the home widget so their
 * initializers can't drift (they already did once, writing NULL_UUID as a real
 * truck_id).
 */
export function getInitialSelectedTruckIds(transaction: {
  truck_id?: string | null;
  truckAttributions?: { truck_id?: string | null }[] | null;
}): string[] {
  const { truckAttributions, truck_id } = transaction;
  if (truckAttributions && truckAttributions.length > 0) {
    return truckAttributions
      .map((attr) => attr.truck_id)
      .filter((id): id is string => !!id && id !== NULL_UUID);
  }
  if (truck_id) return [truck_id];
  return [];
}

/**
 * Collects the resolvable truck records attached to a transaction, so the truck
 * editor can render a name for a selected id even when that truck is gone from
 * the active `/trucks/list` (e.g. a deleted truck).
 *
 * The name is sourced from `truckAttributions[].truck` — the top-level
 * `transaction.truck` association comes down null in the list payload, so it
 * can't be relied on. It's still folded in (deduped) as a harmless fallback in
 * case a caller ever has it populated.
 *
 * Returns resolve-only trucks; the editor never offers these in its dropdown.
 */
export function getAttributionTrucks<
  T extends { truck_id?: string | null },
>(transaction: {
  truck?: T | null;
  truckAttributions?: ({ truck?: T | null } | null)[] | null;
}): T[] {
  const collected: T[] = [];
  const seen = new Set<string>();

  const add = (truck?: T | null) => {
    if (truck && truck.truck_id && !seen.has(truck.truck_id)) {
      seen.add(truck.truck_id);
      collected.push(truck);
    }
  };

  add(transaction.truck);
  transaction.truckAttributions?.forEach((attr) => add(attr?.truck));

  return collected;
}
